import { ArrowLeft, RefreshCw, Settings2 } from "lucide-react";
import React from "react";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { CompanyLogo, getDomainFromCompany } from "../../../../../components/ui/CompanyLogo";

type IntegrationDetailShellProps = {
  integrationName: string;
  connected: boolean;
  lastSync?: string | null;
  credentialOk?: boolean;
  externalId?: string | null;
  reconnectLoading?: boolean;
  reconnectLabel?: string;
  onReconnect?: () => void;
  onBack: () => void;
  showMappingStudioCta?: boolean;
  setupComplete?: boolean;
  onOpenMappingStudio?: () => void;
  children?: React.ReactNode;
};

const IntegrationDetailShell = ({
  integrationName,
  connected,
  lastSync = null,
  credentialOk = true,
  externalId = null,
  reconnectLoading = false,
  reconnectLabel,
  onReconnect,
  onBack,
  showMappingStudioCta = false,
  setupComplete = false,
  onOpenMappingStudio,
  children,
}: IntegrationDetailShellProps) => {
  const statusLabel = connected ? "Connected" : "Disconnected";
  const statusVariant = connected ? "active" : "error";
  const resolvedReconnectLabel = reconnectLabel ?? (connected ? "Reconnect" : "Connect");
  const handleSetupCtaClick = () => {
    if (!connected && onReconnect) {
      onReconnect();
      return;
    }

    onOpenMappingStudio?.();
  };

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Connected Apps
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CompanyLogo
              domain={getDomainFromCompany(integrationName)}
              name={integrationName}
              className="h-9 w-9"
            />
            <div>
              <h1 className="text-[15px] font-semibold text-foreground">{integrationName}</h1>
              <div className="mt-0.5 flex items-center gap-2">
                <Badge variant="statusPill" status={statusVariant}>
                  {statusLabel}
                </Badge>
                {lastSync ? (
                  <span className="text-[11px] text-muted-foreground">Last sync {lastSync}</span>
                ) : null}
              </div>
            </div>
          </div>

          {onReconnect ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-[12px]"
              disabled={reconnectLoading}
              onClick={onReconnect}
            >
              <RefreshCw className={`mr-1.5 h-3 w-3 ${reconnectLoading ? "animate-spin" : ""}`} />
              {resolvedReconnectLabel}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg border border-border">
        <div className="divide-y divide-border">
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[12px] text-muted-foreground">Status</span>
            <span className="text-[12px] text-foreground">{connected ? "Active" : "Inactive"}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[12px] text-muted-foreground">Credentials</span>
            <span className="text-[12px] text-foreground">{credentialOk ? "Valid" : "Invalid"}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[12px] text-muted-foreground">Last Sync</span>
            <span className="text-[12px] text-foreground">{lastSync ?? "Never"}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[12px] text-muted-foreground">External ID</span>
            <span className="font-mono text-[11px] text-foreground">{externalId || "—"}</span>
          </div>
        </div>
      </div>

      {showMappingStudioCta ? (
        <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[13px] font-medium text-foreground">
                {setupComplete ? "Sync Configuration" : "Set up data sync"}
              </h2>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                {setupComplete
                  ? "Configure field mappings, match keys, and write-back rules"
                  : `Map your ${integrationName} fields to Hyperengage in a guided setup`}
              </p>
            </div>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-7 text-[12px]"
              disabled={reconnectLoading}
              onClick={handleSetupCtaClick}
            >
              <Settings2 className="mr-1.5 h-3 w-3" />
              {setupComplete ? "Edit configuration" : "Set up integration"}
            </Button>
          </div>
        </div>
      ) : null}

      {children}
    </div>
  );
};

export default IntegrationDetailShell;
